// src/tools/MeasureReductionHandler.jsx
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { useShallow } from 'zustand/shallow';
import { useEditorStore, useRectificationStore, useAxesAlignmentStore } from '../../../stores';
import { _computeAxes, _computeMLAPFromBuilt, computeSliceCentroidLocal,
  deformPreviewMesh, buildLUTFromRings
 } from '../../../utils/measureHelpers';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { CreateRectificationPreviewMaterial } from '../../../utils/PreviewMaterialsHandler';

const RING_COLORS = ['#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#06b6d4','#84cc16'];
const LINE2_WIDTH = 4.0;   // px — thickness of the visual ring
const CM_TO_UNIT = 1;   // **centimeters scene**: 1 cm = 1 unit

const MeasureReductionHandler = () => {
  const [scene, shadedMesh, material, controls, camera, renderer, replaceShadedMesh, ghostModeEnabled, geometryVersion ] = useEditorStore(useShallow(s => [
    s.sceneRefs.scene,
    s.sceneRefs.shadedMesh,
    s.sceneRefs.material,
    s.sceneRefs.controls,
    s.sceneRefs.camera,
    s.sceneRefs.renderer,
    s.replaceShadedMesh,
    s.ghostModeEnabled,
    s.geometryVersion
  ]));

  const points = useAxesAlignmentStore(useShallow(s => s.selections));
  const [measureState, setMeasureReduction, previewState, blockingMask] = useRectificationStore(
    useShallow(s => [s.measureReduction, s.setMeasureReduction, s.rectifiedPreview, s.blockingZone?.mask])
  );

  const rectifiedMeshRef = useRectificationStore(s => s.rectifiedMeshRef);

  const groupsRef = useRef(null);
  const tcHelperRef = useRef(null);
  const tcRef = useRef(null);
  const activeIdxRef = useRef(null);
  const settingStoreRef = useRef(false);

  // const rectifiedMeshRef = useRef(null);      // cloned mesh for preview
  const originalPosRef   = useRef(null);      // Float32Array snapshot of preview mesh
  const lutRef           = useRef(null);      // {ys[], scales[], centroids[] (local)}

  const originalMaterialRef = useRef(null); // original material of the shadedMesh
  const previewMaterialRef = useRef(null); // material for the rectified preview mesh
  const lastMeshRef = useRef(null); // track mesh changes for centroid recalculation
  const lastGeometryRef = useRef(null); // track geometry changes for preview invalidation
  const lastGeometryVersionRef = useRef(null); // track geometryVersion for cache invalidation
  const lastSyncGeometryVersionRef = useRef(null); // track geometryVersion for centroid sync

  const zeroY = useMemo(() => {
    const sp = points?.subPatellar?.sphere;
    if (sp && sp.isMesh) { const v = new THREE.Vector3(); sp.getWorldPosition(v); return v.y; }
    return 0;
  }, [points?.subPatellar?.sphere]);

  function ensureGroups(){ if(!groupsRef.current){ const g=new THREE.Group(); g.name='MeasureGroups'; scene.add(g); groupsRef.current=g; }}
  function disposeGroups(){ if(!groupsRef.current) return; groupsRef.current.traverse(o=>{o.geometry?.dispose?.(); o.material?.dispose?.();}); groupsRef.current.parent?.remove(groupsRef.current); groupsRef.current=null; }

  // ---------------- helpers ----------------
  const _a=new THREE.Vector3(), _b=new THREE.Vector3(), _c=new THREE.Vector3();
  const _ab=new THREE.Vector3(), _bc=new THREE.Vector3(), _ca=new THREE.Vector3();
  function worldPositionAt(attr,i,mw,out){ out.set(attr.getX(i),attr.getY(i),attr.getZ(i)); return out.applyMatrix4(mw); }

  function orderLoop(points, tol=1e-4){
    if (!points || points.length < 2) return [];
    const rem = points.map(p=>p.clone());
    const loop = [rem.shift()];
    let guard=0;
    while(rem.length && guard++<20000){
      const last = loop[loop.length-1];
      let best=0,bestD=Infinity; for(let i=0;i<rem.length;i++){ const d=last.distanceToSquared(rem[i]); if(d<bestD){bestD=d;best=i;} }
      const next = rem.splice(best,1)[0];
      if (loop.length>2 && next.distanceTo(loop[0])<tol){ loop.push(loop[0].clone()); break; }
      loop.push(next);
    }
    if (loop.length>=2 && loop[0].distanceTo(loop[loop.length-1])>tol) loop.push(loop[0].clone());
    return loop;
  }

  function buildSectionAtY(y, color){
    // Ensure matrixWorld is up-to-date before using it for calculations
    shadedMesh.updateMatrixWorld(true);
    
    const pos = shadedMesh.geometry.attributes.position;
    const index = shadedMesh.geometry.index ? shadedMesh.geometry.index.array : null;
    const mw = shadedMesh.matrixWorld;
    const plane = new THREE.Plane(new THREE.Vector3(0,1,0), -y);

    const tris = index ? index.length/3 : pos.count/3;
    const segs = [];
    let length = 0;
    for(let t=0;t<tris;t++){
      const i0=index?index[t*3+0]:t*3+0, i1=index?index[t*3+1]:t*3+1, i2=index?index[t*3+2]:t*3+2;
      worldPositionAt(pos,i0,mw,_a); worldPositionAt(pos,i1,mw,_b); worldPositionAt(pos,i2,mw,_c);
      const d0=plane.distanceToPoint(_a), d1=plane.distanceToPoint(_b), d2=plane.distanceToPoint(_c);
      const pair=[];
      if (Math.sign(d0)!==Math.sign(d1)) { const t=d0/(d0-d1); pair.push(_ab.copy(_b).sub(_a).multiplyScalar(t).add(_a).clone()); }
      if (Math.sign(d1)!==Math.sign(d2)) { const t=d1/(d1-d2); pair.push(_bc.copy(_c).sub(_b).multiplyScalar(t).add(_b).clone()); }
      if (Math.sign(d2)!==Math.sign(d0)) { const t=d2/(d2-d0); pair.push(_ca.copy(_a).sub(_c).multiplyScalar(t).add(_c).clone()); }
      if (pair.length===2){ segs.push(pair[0], pair[1]); length += pair[0].distanceTo(pair[1]); }
    }

    // centroid
    const centroid = new THREE.Vector3();
    for (let i=0;i<segs.length;i+=2) centroid.add(segs[i]).add(segs[i+1]);
    const N = Math.max(segs.length,1); centroid.multiplyScalar(1/N); centroid.y = y;

    // thin (pickable) segments
    const thinPos = new Float32Array(segs.length*3);
    for (let i=0;i<segs.length;i++){ const p=segs[i]; thinPos[i*3]=p.x-centroid.x; thinPos[i*3+1]=p.y-centroid.y; thinPos[i*3+2]=p.z-centroid.z; }
    const gThin = new THREE.BufferGeometry(); if (segs.length) gThin.setAttribute('position', new THREE.BufferAttribute(thinPos,3));
    const mThin = new THREE.LineBasicMaterial({ color, transparent:true, opacity:0.001, depthTest:false });
    const thin = new THREE.LineSegments(gThin, mThin); thin.name='MeasureRingLine_thin'; thin.renderOrder=9e5;

    // fat (visual) ordered loop
    let fat=null; const loop = orderLoop(segs);
    if (loop.length>=2){
      const fatPos = new Float32Array(loop.length*3);
      for (let i=0;i<loop.length;i++){ const p=loop[i]; fatPos[i*3]=p.x-centroid.x; fatPos[i*3+1]=p.y-centroid.y; fatPos[i*3+2]=p.z-centroid.z; }
      const geo = new LineGeometry(); geo.setPositions(fatPos);
      const mat = new LineMaterial({ color, linewidth: LINE2_WIDTH, transparent:true, opacity:0.98, depthTest:false });
      const el = renderer?.domElement; if (el) mat.resolution.set(el.clientWidth, el.clientHeight);
      fat = new Line2(geo, mat); fat.computeLineDistances(); fat.name='MeasureRingLine_fat'; fat.renderOrder=9e5; fat.frustumCulled=false;
    }

    return { thin, fat, centroid, length };
  }

  const round1 = (v)=> Math.round(v*10)/10;

  function persistHeight(index, yMeters){
    const latest = useRectificationStore.getState().measureReduction;
    const m = latest.measurements[index]; if (!m) return;
    const scanLenM = 0; // untouched here; syncAt will compute full values
    const arr = [...latest.measurements];
    arr[index] = { ...m, height: round1((yMeters - zeroY) / CM_TO_UNIT) };
    settingStoreRef.current = true;
    setMeasureReduction({ measurements: arr, currentIndex: activeIdxRef.current ?? latest.currentIndex });
    queueMicrotask(()=>{ settingStoreRef.current = false; });
  }

  function syncAt(index, opts={ resetRectified:false, forceRecalculate:false }){
    if (!groupsRef.current || !shadedMesh) return;

    // get the current measurement and group from the state and ref respect
    const latest = useRectificationStore.getState().measureReduction;
    const m = latest.measurements[index]; const grp = groupsRef.current.children[index]; if(!m||!grp) return;
    const y = grp.position.y; // meters

    const oldThin = grp.getObjectByName('MeasureRingLine_thin'); if (oldThin){ oldThin.geometry?.dispose?.(); oldThin.material?.dispose?.(); grp.remove(oldThin); }
    const oldFat  = grp.getObjectByName('MeasureRingLine_fat');  if (oldFat) { oldFat.geometry?.dispose?.();  oldFat.material?.dispose?.();  grp.remove(oldFat); }

    const color = RING_COLORS[index % RING_COLORS.length];
    const built = buildSectionAtY(y, color);
    grp.position.set(built.centroid.x, y, built.centroid.z);
    grp.add(built.thin); if (built.fat) grp.add(built.fat);
    grp.updateMatrixWorld(true);

    const centroidLocal = computeSliceCentroidLocal(shadedMesh, grp);

    // write back full measurement values (height in cm)
    const scanLenM = built.length; // meters
    let rectifiedM, pct;
    if(opts.resetRectified){
      rectifiedM = scanLenM; // reset to original scan length
      pct = 0; // reset percentage
    } else{
      const rectFromPctM = scanLenM * (1 + (m.percentage ?? 0)/100);
      rectifiedM   = m.__editedCm ? (m.rectified?.circumference ?? scanLenM) : rectFromPctM;
      pct          = ((rectifiedM / Math.max(scanLenM, 1e-9)) - 1) * 100;
    }

    //Axes for Landmarks
    const { uML, uAP } = _computeAxes(points);
    const { mlM, apM } = _computeMLAPFromBuilt(built, uML, uAP);
    const scale = scanLenM > 0 ? (rectifiedM / scanLenM) : 1;

    const arr = [...latest.measurements];
    arr[index] = {
      ...m,
      height: round1((y - zeroY) / CM_TO_UNIT), // cm
      scan: { 
        circumference: round1(scanLenM), 
        medioLat: round1(mlM), 
        antPost: round1(apM) 
      }, 
      rectified: { 
        circumference: round1(rectifiedM), 
        medioLat: round1(mlM * scale), 
        antPost: round1(apM * scale) 
      },
      percentage: round1(pct),
      falloff: m.falloff || 1.0, // preserve falloff value, default to 1.0 if not set
      _centroidLocal: opts.forceRecalculate ? centroidLocal : (centroidLocal || m._centroidLocal), //cache for deformer
      __editedCm: opts.resetRectified ? false : m.__editedCm,
    };

    settingStoreRef.current = true;
    setMeasureReduction({ measurements: arr, currentIndex: latest.currentIndex });

    queueMicrotask(()=>{ settingStoreRef.current = false; });
  }

  function ensurePreviewMesh(scene, shadedMesh){
    // Check if geometry has changed (e.g., after alignment transformation)
    // Now also check geometryVersion which tracks in-place geometry modifications
    const geometryChanged = (lastGeometryRef.current &&
      shadedMesh &&
      shadedMesh.geometry.attributes.position !== lastGeometryRef.current) ||
      (lastGeometryVersionRef.current !== null && lastGeometryVersionRef.current !== geometryVersion);

    // Invalidate cached preview if geometry changed
    if (geometryChanged && rectifiedMeshRef.current) {
      console.log('🔄 Invalidating preview mesh due to geometry change (geometryVersion:', geometryVersion, ')');
      if (rectifiedMeshRef.current.parent) {
        rectifiedMeshRef.current.parent.remove(rectifiedMeshRef.current);
      }
      // **CRITICAL FIX**: Dispose BVH tree before disposing geometry
      if (rectifiedMeshRef.current.geometry?.boundsTree) {
        rectifiedMeshRef.current.geometry.disposeBoundsTree();
      }
      rectifiedMeshRef.current = null;
      originalPosRef.current = null;
    }

    if (rectifiedMeshRef.current && rectifiedMeshRef.current.parent) return rectifiedMeshRef.current;

    if (!shadedMesh) return null;
    const cloneGeom = shadedMesh.geometry.clone();
    let cloneMat = previewMaterialRef.current;
    if (!cloneMat) {
      cloneMat = CreateRectificationPreviewMaterial(0.6);
    }
    if (!originalMaterialRef.current) originalMaterialRef.current = material; // store original material
    previewMaterialRef.current = cloneMat; // store preview material

    const clone = new THREE.Mesh(cloneGeom, cloneMat);
    clone.name = 'RectifiedPreview';
    // inherit TRS
    clone.matrixAutoUpdate = true;
    clone.position.copy(shadedMesh.position);
    clone.quaternion.copy(shadedMesh.quaternion);
    clone.scale.copy(shadedMesh.scale);

    rectifiedMeshRef.current = clone;
    originalPosRef.current = clone.geometry.attributes.position.array.slice(); // snapshot
    lastGeometryRef.current = shadedMesh.geometry.attributes.position; // track current geometry
    lastGeometryVersionRef.current = geometryVersion; // track current geometry version
    scene.add(clone);
    // Ensure matrixWorld is immediately updated after adding to scene
    // This is critical for initialization - subsequent deformation calculations depend on it
    clone.updateMatrixWorld(true);
    return clone;
  }

  function resetPreviewMesh(){
    const m = rectifiedMeshRef.current; const base = originalPosRef.current;
    if (!m || !base) return;
    m.geometry.attributes.position.set(base);
    m.geometry.attributes.position.needsUpdate = true;
    m.geometry.computeVertexNormals();
  }

  function applyBlockingMaskToPreview(){
    const preview = rectifiedMeshRef.current;
    if (!preview || !originalPosRef.current) return;
    if (!blockingMask || blockingMask.length === 0) return;

    const posAttr = preview.geometry.attributes.position;
    const basePos = originalPosRef.current;
    const count = posAttr.count;

    // Apply mask: restore blocked vertices to original positions
    for (let i = 0; i < count; i++){
      if (i * 3 + 2 >= basePos.length) continue; // safety check

      // mask: 0 = fully active (allow deformation), 1 = fully blocked (restore to original)
      const m = Math.min(1, Math.max(0, blockingMask[i] || 0));

      if (m > 1e-6) { // only restore if significantly blocked
        const i3 = i * 3;
        const deformedX = posAttr.getX(i);
        const deformedY = posAttr.getY(i);
        const deformedZ = posAttr.getZ(i);

        // Blend between deformed and original based on blocking strength
        const x = deformedX * (1 - m) + basePos[i3 + 0] * m;
        const y = deformedY * (1 - m) + basePos[i3 + 1] * m;
        const z = deformedZ * (1 - m) + basePos[i3 + 2] * m;

        posAttr.setXYZ(i, x, y, z);
      }
    }

    posAttr.needsUpdate = true;
    preview.geometry.computeVertexNormals();
  }

  // ---------------- mount ----------------
  // 1) Create/remove the preview clone
  useEffect(()=>{
    if (!scene || !shadedMesh) return;
    const preview = ensurePreviewMesh(scene, shadedMesh);
    if (preview) preview.visible = !!previewState.enabled;
    return () => {
      if (rectifiedMeshRef.current && rectifiedMeshRef.current.parent) {
        rectifiedMeshRef.current.parent.remove(rectifiedMeshRef.current);
      }
      // **CRITICAL FIX**: Dispose BVH tree on cleanup
      if (rectifiedMeshRef.current?.geometry?.boundsTree) {
        rectifiedMeshRef.current.geometry.disposeBoundsTree();
      }
      rectifiedMeshRef.current = null;
      originalPosRef.current = null;
    };
  }, [scene, shadedMesh, previewState.enabled]);

  // Adjust preview mesh material based on ghost mode
  useEffect(() => {
    const previewMesh = rectifiedMeshRef.current;

    // Only update if preview mesh exists
    if (!previewMesh || !previewMesh.material) return;

    if (ghostModeEnabled) {
      // Ghost mode ON: Make preview completely opaque
      previewMesh.material.opacity = 1.0;
      previewMesh.material.transparent = false;
    } else {
      // Ghost mode OFF: Make preview transparent
      previewMesh.material.opacity = 0.6;
      previewMesh.material.transparent = true;
    }

    previewMesh.material.needsUpdate = true;
  }, [ghostModeEnabled]);

  // 2) Rebuild LUT + deform whenever rings or preview toggle change
  useEffect(()=>{
    if (!scene || !shadedMesh) return;
    const preview = ensurePreviewMesh(scene, shadedMesh);
    if (!preview) return;

    // if (!previewState.enabled) {
    //   resetPreviewMesh();
    //   return;
    // }

    // Only build LUT if groups exist (meaning measurements have been processed)
    if (groupsRef.current && groupsRef.current.children.length > 0) {
      const allSeeded = measureState.measurements.every(m => m._centroidLocal);
      if(!allSeeded) return;
      // build LUT from current rings (uses cached per-ring centroid from syncAt)
      const lut = buildLUTFromRings(shadedMesh, zeroY, measureState.measurements, 1, 'hann');
      lutRef.current = lut;
      deformPreviewMesh(preview, lut, originalPosRef.current);
      // Apply blocking zones to respect user's paint masks
      applyBlockingMaskToPreview();
    }

  }, [scene, shadedMesh, zeroY, measureState.measurements, previewState.enabled, groupsRef.current, geometryVersion, blockingMask]);

  useEffect(()=>{
    if (!scene || !shadedMesh || !camera || !renderer) return; ensureGroups();

    groupsRef.current.children.slice().forEach(c=>c.parent.remove(c));
    measureState.measurements.forEach((m,i)=>{
      const y = zeroY + (m.height ?? 0) * CM_TO_UNIT; // cm→m
      const grp = new THREE.Group(); grp.name=`MeasureGroup_${i}`; grp.position.y = y;
      const built = buildSectionAtY(y, RING_COLORS[i % RING_COLORS.length]); grp.position.set(built.centroid.x, y, built.centroid.z); grp.add(built.thin); if (built.fat) grp.add(built.fat);
      groupsRef.current.add(grp);
    });

    // After creating groups, ensure all measurements have centroidLocal calculated
    measureState.measurements.forEach((m,i)=>{
      if (!m._centroidLocal) {
        syncAt(i, { forceRecalculate: true });
      }
    });

    const tc = new TransformControls(camera, renderer.domElement);
    tc.setMode('translate'); tc.showX=false; tc.showZ=false; tc.showY=true;

    // keep orbit/controls disabled during drag
    tc.addEventListener('dragging-changed', e=>{ if (controls) controls.enabled = !e.value; });

    // persist continuously while dragging
    tc.addEventListener('objectChange', ()=>{ if (activeIdxRef.current!=null) syncAt(activeIdxRef.current, { resetRectified: true }); });

    // also persist once more when the drag ends (extra safety)
    tc.addEventListener('mouseUp', ()=>{ if (activeIdxRef.current!=null){ const grp = groupsRef.current.children[activeIdxRef.current]; if (grp) persistHeight(activeIdxRef.current, grp.position.y); } });

    const helper = tc.getHelper(); helper.name='Measure_TC_Helper'; scene.add(helper); tcRef.current = tc; tcHelperRef.current = helper;

    // select by clicking the thin pick line
    const onPointerDown = (ev)=>{
      const el = renderer?.domElement; if (!el) return; const r = el.getBoundingClientRect();
      const ndc = new THREE.Vector2(((ev.clientX-r.left)/r.width)*2-1, -((ev.clientY-r.top)/r.height)*2+1);
      const ray = new THREE.Raycaster(); ray.setFromCamera(ndc, camera);
      const pick = groupsRef.current.children.map(g=>g.getObjectByName('MeasureRingLine_thin')).filter(Boolean);
      const hits = ray.intersectObjects(pick, false);
      if (hits[0]){
        const obj = hits[0].object; const idx = groupsRef.current.children.findIndex(g=>g.getObjectByName('MeasureRingLine_thin')===obj);
        if (idx!==-1){
          activeIdxRef.current = idx;
          tc.attach(groupsRef.current.children[idx]);
          // ---- sync selection into zustand ----
          setMeasureReduction({ currentIndex: idx });
          if (controls) controls.enabled=false; window.addEventListener('pointerup',()=>{ if (controls) controls.enabled=true; },{once:true});
        }
      }
    };
    renderer.domElement.addEventListener('pointerdown', onPointerDown);

    return ()=>{
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      tc.detach(); tc.dispose?.(); if (tcHelperRef.current){ tcHelperRef.current.parent?.remove(tcHelperRef.current); tcHelperRef.current=null; }
      disposeGroups();
    };
  }, [scene, shadedMesh, camera, renderer, controls, measureState.measurements.length, zeroY, geometryVersion]);

  // respond to store edits (UI typing / add new measure) and mesh changes
  useEffect(()=>{
    if (!shadedMesh || !groupsRef.current) return; if (settingStoreRef.current) return;

    // Check if mesh has changed (e.g., after undo operation)
    const meshChanged = lastMeshRef.current !== shadedMesh;
    // Check if geometry has been modified in-place (e.g., after alignment)
    const geometryVersionChanged = lastSyncGeometryVersionRef.current !== null &&
                                    lastSyncGeometryVersionRef.current !== geometryVersion;

    if (meshChanged || geometryVersionChanged) {
      if (meshChanged) {
        lastMeshRef.current = shadedMesh;
      }
      if (geometryVersionChanged) {
        console.log('🔄 Geometry version changed, recalculating all centroidLocal values (geometryVersion:', geometryVersion, ')');
        lastSyncGeometryVersionRef.current = geometryVersion;
      }
      // Force recalculation of all centroidLocal values when mesh or geometry changes
      measureState.measurements.forEach((m,i)=>{
        const grp = groupsRef.current.children[i]; if (!grp) return;
        syncAt(i, { forceRecalculate: true });
      });
    } else {
      // Normal measurement updates
      measureState.measurements.forEach((m,i)=>{
        const grp = groupsRef.current.children[i]; if (!grp) return;
        const wantY = zeroY + (m.height ?? 0) * CM_TO_UNIT; // cm→m
        if (Math.abs(grp.position.y - wantY) > 1e-6) grp.position.y = wantY;
        syncAt(i);
      });
    }
  }, [measureState.measurements, zeroY, shadedMesh, geometryVersion]);

  // Listen to currentIndex changes from external sources (like UI) and attach transform controls
  useEffect(() => {
    if (!tcRef.current || !groupsRef.current || settingStoreRef.current) return;
    
    const currentIndex = measureState.currentIndex;
    const tc = tcRef.current;
    
    // Only update if the index is different from what we're currently tracking
    if (activeIdxRef.current !== currentIndex && currentIndex >= 0 && currentIndex < groupsRef.current.children.length) {
      activeIdxRef.current = currentIndex;
      const targetGroup = groupsRef.current.children[currentIndex];
      if (targetGroup) {
        tc.attach(targetGroup);
      }
    }
  }, [measureState.currentIndex]);

  useEffect(() => {

    return () => {
      // **CRITICAL FIX**: Comprehensive cleanup on unmount
      if(previewMaterialRef.current) {
        previewMaterialRef.current.dispose();
        previewMaterialRef.current = null;
      }
      if( groupsRef.current ) {
        groupsRef.current.traverse(o => {
          o.geometry?.dispose?.();
          o.material?.dispose?.();
        });
        groupsRef.current.parent?.remove(groupsRef.current);
        groupsRef.current = null;
      }
      if( tcRef.current ) {
        tcRef.current.detach();
        tcRef.current.dispose?.();
        if( tcHelperRef.current ) {
          tcHelperRef.current.parent?.remove(tcHelperRef.current);
          tcHelperRef.current = null;
        }
        tcRef.current = null;
      }
      // **CRITICAL FIX**: Ensure preview mesh is fully cleaned up including BVH
      if(rectifiedMeshRef.current) {
        if(rectifiedMeshRef.current.parent) {
          rectifiedMeshRef.current.parent.remove(rectifiedMeshRef.current);
        }
        if(rectifiedMeshRef.current.geometry?.boundsTree) {
          rectifiedMeshRef.current.geometry.disposeBoundsTree();
        }
        if(rectifiedMeshRef.current.geometry) {
          rectifiedMeshRef.current.geometry.dispose();
        }
        if(rectifiedMeshRef.current.material) {
          rectifiedMeshRef.current.material.dispose();
        }
        rectifiedMeshRef.current = null;
      }
    }
  }, [])

  return null;
}

export default MeasureReductionHandler;
